import './submitButtonLoader.css'

// Global loading state for primary (save / submit) buttons.
//
// When a primary button is clicked, every API request started shortly after that click is
// attributed to it. While those requests are in flight the button shows a spinner and any
// further click on it is swallowed, so the form cannot be submitted twice. Buttons whose click
// does not trigger a request (validation errors, opening a modal, ...) are left untouched.
//
// Opt in any other button with `data-submit-loader`, opt out a primary button with
// `data-no-submit-loader`.

const BUTTON_SELECTOR = '.btn.btn-primary, [data-submit-loader]'
const SKIP_SELECTOR = '[data-no-submit-loader]'
const LOADING_ATTR = 'data-submit-loading'

// Time after a click during which a new request is attributed to the clicked button
const ARM_WINDOW_MS = 1000
// Time the button stays locked after its last request settles, so chained requests
// (e.g. save then reload) keep the same loading state without flickering
const SETTLE_GRACE_MS = 300

type Entry = {
  el: HTMLElement
  pending: number
  releaseTimer?: number
}

const entries = new Map<HTMLElement, Entry>()
let armed: { el: HTMLElement; until: number } | null = null
let installed = false

const arm = (el: HTMLElement, ms: number) => {
  armed = { el, until: Date.now() + ms }
}

const getArmedEntry = (): Entry | null => {
  if (!armed || Date.now() > armed.until) return null
  let entry = entries.get(armed.el)
  if (!entry) {
    entry = { el: armed.el, pending: 0 }
    entries.set(armed.el, entry)
  }
  return entry
}

const setLoading = (el: HTMLElement, loading: boolean) => {
  if (loading) {
    el.setAttribute(LOADING_ATTR, '')
    el.setAttribute('aria-busy', 'true')
    el.setAttribute('aria-disabled', 'true')
  } else {
    el.removeAttribute(LOADING_ATTR)
    el.removeAttribute('aria-busy')
    el.removeAttribute('aria-disabled')
  }
}

const begin = (entry: Entry) => {
  window.clearTimeout(entry.releaseTimer)
  entry.pending++
  setLoading(entry.el, true)
}

const end = (entry: Entry) => {
  entry.pending = Math.max(0, entry.pending - 1)
  if (entry.pending > 0) return
  arm(entry.el, SETTLE_GRACE_MS)
  entry.releaseTimer = window.setTimeout(() => {
    if (entry.pending > 0) return
    setLoading(entry.el, false)
    entries.delete(entry.el)
  }, SETTLE_GRACE_MS)
}

const onClickCapture = (e: MouseEvent) => {
  const target = e.target as Element | null
  const button = target?.closest?.(BUTTON_SELECTOR) as HTMLElement | null
  if (!button || button.matches(SKIP_SELECTOR)) return

  // Already submitting: block the click before React (or a form submit) sees it
  if (button.hasAttribute(LOADING_ATTR)) {
    e.preventDefault()
    e.stopImmediatePropagation()
    return
  }
  arm(button, ARM_WINDOW_MS)
}

export const installSubmitButtonLoader = () => {
  if (installed || typeof window === 'undefined') return
  installed = true

  document.addEventListener('click', onClickCapture, true)

  const originalFetch = window.fetch.bind(window)
  window.fetch = (...args: Parameters<typeof fetch>) => {
    const entry = getArmedEntry()
    const request = originalFetch(...args)
    if (entry) {
      begin(entry)
      const done = () => end(entry)
      request.then(done, done)
    }
    return request
  }
}
